import { localizedStrings } from '@core/strings'
import React from 'react'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingButton,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import { Text } from '@fundamentals'
import { FormControl, InputLabel, MenuItem } from '@mui/material'
import Select from '@mui/material/Select'
import { Controller, useForm } from 'react-hook-form'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'

type ApproverOption = { id: number; name: string }

type NominateApproverModalProps = {
  approvalLabel?: string
  description?: string
  suggestedApprovers: ApproverOption[]
  targetApprovers?: ApproverOption[]
  onDismiss?: () => void
  onNominate: (approvers: Array<{ targetApproverId: number }>) => Promise<void>
}

type FormValues = {
  approverIds: number[]
}

const NominateApproverModal: React.FC<NominateApproverModalProps> = ({
  approvalLabel,
  description = localizedStrings.notifyApproversText,
  suggestedApprovers,
  targetApprovers = [],
  onDismiss = () => {},
  onNominate,
}) => {
  const { hideModal } = useModal()
  const toast = useToast()

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      approverIds: targetApprovers.map(({ id }) => id),
    },
  })

  const onSkip = () => {
    hideModal()
    onDismiss()
  }

  const onSubmit = async ({ approverIds }: FormValues) => {
    const filteredApprovers = approverIds
      .filter(
        (approverId) => !targetApprovers.find(({ id }) => id === approverId),
      )
      .map((approverId) => ({ targetApproverId: approverId }))

    if (filteredApprovers.length) {
      try {
        await onNominate(filteredApprovers)
      } catch (e) {
        toast.error(e.message)
      }
    } else {
      onSkip()
    }
  }

  const renderValue = (value: number[]) => {
    return value
      .map((v) => {
        return suggestedApprovers.find((option) => option.id === v)?.name
      })
      .join(', ')
  }

  const getTitle = (approvalLabel?: string): string => {
    let title = localizedStrings.notifyApprovers
    if (approvalLabel && approvalLabel !== localizedStrings.defaultApproval) {
      title += ` – ${approvalLabel}`
    }
    return title
  }

  return (
    <ModalDialogContainer preventDialogClose>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-test='nominate-approver-form'
      >
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20 }}
        >
          {getTitle(approvalLabel)}
          <IconButton
            aria-label='close-modal'
            onClick={onSkip}
            data-test='close-btn'
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
        >
          <Text mb={3}>{description}</Text>
          <FormControl fullWidth>
            <InputLabel id='select-label'>
              {localizedStrings.approver}
            </InputLabel>
            <Controller
              name='approverIds'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  displayEmpty={!!field.value?.length}
                  labelId='select-label'
                  id='approvers'
                  label={localizedStrings.approver}
                  renderValue={renderValue}
                  data-test='select-approver'
                  multiple
                >
                  {suggestedApprovers.map((v, index) => (
                    <MenuItem
                      value={v.id}
                      key={v.id}
                      data-test={`select-approver-option-${index}`}
                      disabled={
                        !!targetApprovers.find(
                          (targetApprover) => targetApprover.id === v.id,
                        )
                      }
                    >
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button variant='outlined' onClick={onSkip} data-test='skip-btn'>
            {localizedStrings.notifyAll}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
            disabled={!isValid && !isDirty}
            data-test='confirm-btn'
          >
            {localizedStrings.confirm}
          </LoadingButton>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}

export default NominateApproverModal
